import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Loader2 } from 'lucide-react';
import { useLanguage, SPECIALTY_PHRASES } from '../../context/LanguageContext';
import type { Therapist } from '../../types';

interface NewTherapist {
  name: string;
  phone_number: string;
  description: string;
  photo_url: string;
  line_user_id?: string;
}

interface AddTherapistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newTherapist: NewTherapist;
  setNewTherapist: (t: NewTherapist) => void;
  handlePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>, isEdit?: boolean) => void;
  uploadingPhoto: boolean;
  addingTherapist: boolean;
  handleAddTherapist: () => void;
}

export const AddTherapistDialog = ({
  open, onOpenChange, newTherapist, setNewTherapist,
  handlePhotoUpload, uploadingPhoto, addingTherapist, handleAddTherapist
}: AddTherapistDialogProps) => {
  const { t, translateDescription } = useLanguage();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.addNewTherapist')}</DialogTitle>
          <DialogDescription>{t('admin.therapistDetails')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="therapist-name">{t('admin.therapistName')} *</Label>
            <Input id="therapist-name" value={newTherapist.name}
              onChange={(e) => setNewTherapist({...newTherapist, name: e.target.value})}
              placeholder={t('admin.therapistName')} data-testid="new-therapist-name" />
          </div>
          <div>
            <Label htmlFor="therapist-phone">{t('admin.therapistPhone')} *</Label>
            <Input id="therapist-phone" value={newTherapist.phone_number}
              onChange={(e) => setNewTherapist({...newTherapist, phone_number: e.target.value})}
              placeholder="0971344376" data-testid="new-therapist-phone" />
          </div>
          <div>
            <Label htmlFor="therapist-photo">{t('admin.photoUrl')}</Label>
            <div className="flex items-center gap-4">
              {newTherapist.photo_url && (
                <img src={newTherapist.photo_url} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
              )}
              <div className="flex-1">
                <input type="file" id="therapist-photo" accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handlePhotoUpload(e, false)} className="hidden" data-testid="new-therapist-photo-input" />
                <Button type="button" variant="outline"
                  onClick={() => document.getElementById('therapist-photo').click()} disabled={uploadingPhoto} className="w-full">
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploadingPhoto ? t('admin.uploading') : t('admin.uploadPhoto')}
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="therapist-desc">{t('admin.description')}</Label>
            <Select value={newTherapist.description || undefined}
              onValueChange={(v) => setNewTherapist({...newTherapist, description: v})}>
              <SelectTrigger id="therapist-desc" data-testid="new-therapist-desc">
                <SelectValue placeholder={t('admin.selectSpecialty')} />
              </SelectTrigger>
              <SelectContent>
                {SPECIALTY_PHRASES.map((phrase) => (
                  <SelectItem key={phrase} value={phrase}
                    data-testid={`specialty-option-${phrase.toLowerCase().replace(/\s+/g, '-')}`}>
                    {translateDescription(phrase)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleAddTherapist} disabled={addingTherapist || uploadingPhoto} data-testid="save-therapist-btn">
            {addingTherapist ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {t('admin.addTherapistBtn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface EditTherapistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editData: NewTherapist & { id?: string };
  setEditData: (t: any) => void;
  handlePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>, isEdit?: boolean) => void;
  uploadingEditPhoto: boolean;
  savingTherapist: boolean;
  handleSaveTherapist: () => void;
}

export const EditTherapistDialog = ({
  open, onOpenChange, editData, setEditData,
  handlePhotoUpload, uploadingEditPhoto, savingTherapist, handleSaveTherapist
}: EditTherapistDialogProps) => {
  const { t, translateDescription } = useLanguage();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.editTherapist')}</DialogTitle>
          <DialogDescription>{t('admin.editTherapistDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-therapist-name">{t('admin.therapistName')} *</Label>
            <Input id="edit-therapist-name" value={editData.name}
              onChange={(e) => setEditData({...editData, name: e.target.value})}
              placeholder={t('admin.therapistName')} data-testid="edit-therapist-name" />
          </div>
          <div>
            <Label htmlFor="edit-therapist-phone">{t('admin.therapistPhone')} *</Label>
            <Input id="edit-therapist-phone" value={editData.phone_number}
              onChange={(e) => setEditData({...editData, phone_number: e.target.value})}
              placeholder="0971344376" data-testid="edit-therapist-phone" />
          </div>
          <div>
            <Label htmlFor="edit-therapist-line">{t('admin.lineIdLabel')}</Label>
            <Input id="edit-therapist-line" value={editData.line_user_id}
              onChange={(e) => setEditData({...editData, line_user_id: e.target.value})}
              placeholder="e.g. @angelmassage" data-testid="edit-therapist-line" />
            <p className="text-xs text-muted-foreground mt-1">{t('admin.lineIdHelp')}</p>
          </div>
          <div>
            <Label htmlFor="edit-therapist-photo-upload">{t('admin.photoUrl')}</Label>
            <div className="flex items-center gap-4">
              {editData.photo_url && (
                <img src={editData.photo_url} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
              )}
              <div className="flex-1">
                <input type="file" id="edit-therapist-photo-upload" accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handlePhotoUpload(e, true)} className="hidden" data-testid="edit-therapist-photo-input" />
                <Button type="button" variant="outline"
                  onClick={() => document.getElementById('edit-therapist-photo-upload').click()}
                  disabled={uploadingEditPhoto} className="w-full">
                  {uploadingEditPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploadingEditPhoto ? t('admin.uploading') : t('admin.changePhoto')}
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="edit-therapist-desc">{t('admin.description')}</Label>
            <Select value={editData.description || undefined}
              onValueChange={(v) => setEditData({...editData, description: v})}>
              <SelectTrigger id="edit-therapist-desc" data-testid="edit-therapist-desc">
                <SelectValue placeholder={t('admin.selectSpecialty')} />
              </SelectTrigger>
              <SelectContent>
                {SPECIALTY_PHRASES.map((phrase) => (
                  <SelectItem key={phrase} value={phrase}
                    data-testid={`edit-specialty-option-${phrase.toLowerCase().replace(/\s+/g, '-')}`}>
                    {translateDescription(phrase)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveTherapist} disabled={savingTherapist || uploadingEditPhoto} data-testid="update-therapist-btn">
            {savingTherapist ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {t('admin.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface AssignTherapistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: { name: string } | null;
  therapists: Therapist[];
  selectedTherapist: string;
  setSelectedTherapist: (id: string) => void;
  assigning: boolean;
  handleAssignTherapist: () => void;
  SelectComponents: any;
}

export const AssignTherapistDialog = ({
  open, onOpenChange, booking, therapists,
  selectedTherapist, setSelectedTherapist,
  assigning, handleAssignTherapist,
  SelectComponents
}: AssignTherapistDialogProps) => {
  const { t } = useLanguage();
  const { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } = SelectComponents;
  return (
    <Dialog open={open} onOpenChange={(o) => onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.assignTherapist')}</DialogTitle>
          <DialogDescription>
            {t('admin.assignBookingFor')} {booking?.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Select value={selectedTherapist} onValueChange={setSelectedTherapist}>
            <SelectTrigger data-testid="select-therapist">
              <SelectValue placeholder={t('admin.selectATherapist')} />
            </SelectTrigger>
            <SelectContent>
              {therapists
                .filter(th => th.availability_status === 'available')
                .map((th) => (
                  <SelectItem key={th.id} value={th.id}>
                    {th.name} {th.line_user_id ? '' : `(${t('admin.lineNotLinked')})`}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {therapists.filter(th => th.availability_status === 'available').length === 0 && (
            <p className="text-sm text-orange-600">{t('admin.noAvailableTherapists')}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleAssignTherapist} disabled={assigning || !selectedTherapist} data-testid="confirm-assign-btn">
            {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {t('admin.assignTherapist')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
